import debouce from 'lodash.debounce';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { AnimatedDashboard } from '../../components';
import Spinner from '../../components/common/Spinner';
import { HELPDESK_QUESTION, HELPDESK_SUBCATEGORY } from '../../routes/routes';
import { clearQuestions } from '../../slices/help-desk-slice';
import { colors } from '../../styles/GlobalStyles';
import { Icon, IconSearch } from '../../styles/Icons';
import {
  HelpDeskContainer,
  SearchContainer,
  SearchInput,
  TopicCategory,
  TopicCategoryTitle
} from './HelpDeskStyled';
import { SearchDropdown } from './SearchDropdown';
import { TableContentRoot } from './TableContent';
import { getSupportCategories, searchQuestion } from './helpDesk.actions';
import { Category } from './helpDesk.types';

type TParams = { categoryId?: string };

export const HelpDesk = (props: RouteComponentProps<TParams>) => {
  const { t } = useTranslation('helpdesk');
  const [close] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useAppDispatch();

  const onChange = (e: any) => {
    if (e.target.value.length > 0) {
      dispatch(searchQuestion(e.target.value));
    } else {
      dispatch(clearQuestions());
    }
  };

  const debouncedResultsContact = useMemo(() => {
    return debouce(onChange, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(getSupportCategories(props));
  }, [dispatch, props]);

  useEffect(() => {
    return () => {
      debouncedResultsContact.cancel();
    };
  });

  const isEmpty = (category: Category) => {
    return Object.keys(category).length === 0;
  };

  const { category, categories, loading, questions } = useAppSelector(
    (state) => state.helpDesk
  );

  if (loading) return <Spinner fullScreen={true} />;

  return (
    <AnimatedDashboard zoom dark>
      <div style={{ width: '100%' }}>
        <SearchContainer close={close}>
          <Icon mr='15' color={colors.neutral700}>
            <IconSearch />
          </Icon>

          <SearchInput
            type='text'
            onChange={debouncedResultsContact}
            placeholder={t('search_articles_placeholder')}
            onFocus={() => setIsOpen(true)}
          />
          <SearchDropdown
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            questions={questions}
            category={category}
          />
        </SearchContainer>

        <HelpDeskContainer close={close}>
          {category && !isEmpty(category) && (
            <>
              <TopicCategory>
                <TopicCategoryTitle>
                  <h1>{category?.name}</h1>
                </TopicCategoryTitle>
                {category?.subcategories?.map((subcategory, i) => (
                  <Link
                    to={`${HELPDESK_SUBCATEGORY}/${category.id}/${subcategory.id}`}
                    key={i}>
                    <li>{subcategory.name}</li>
                  </Link>
                ))}
                {category?.questions?.map((question, i) => (
                  <Link
                    to={`${HELPDESK_QUESTION}/${question.id}/${category.id}`}
                    key={i}>
                    <li>{question.question}</li>
                  </Link>
                ))}
              </TopicCategory>
              <TableContentRoot categories={categories} id={category?.id} />
            </>
          )}
        </HelpDeskContainer>
      </div>
    </AnimatedDashboard>
  );
};
